import { IconDefinition } from '@fortawesome/fontawesome-common-types';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Dropdown from '@ui/Dropdown';
import { cx } from 'class-variance-authority';
import React, { useRef } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useInputError } from './hooks';

export type Option = {
    key: string | number;
    label: string;
    color?: any;
    icon?: IconDefinition;
};

interface Props {
    /** @deprecated */
    dropdownCss?: any;
    name: string;
    label?: string | React.ReactNode;
    description?: string | React.ReactNode;
    options: Option[];
    buttonProps?: Partial<any>;
    defaultValue?: string | number;
    className?: string;
    required?: boolean;
}

const FormSelectMultiple = ({ name, label, description, options, buttonProps, className, required }: Props) => {
    const { t } = useTranslation('form');
    const { control } = useFormContext();
    const dropdownRef = useRef<{ open: () => void; close: () => void } | null>(null);
    const getObjForValue = (key: string) =>
        options?.find((o) => String(o.key).toLowerCase() === String(key).toLowerCase());
    const { message: errorMessage } = useInputError(name);
    const {
        field: { value: _value, onChange }
    } = useController({
        control,
        name,
        rules: required
            ? { validate: (selected) => (Array.isArray(selected) && selected.length > 0) || t('select.required') }
            : undefined
    });
    // this conversion fixes bugs when json is wrongly formatted from backend
    const value = Array.isArray(_value) ? _value : Object.values(_value ?? {});

    const {
        className: dropdownButtonClassName,
        containerClassName,
        disabled: buttonDisabled,
        isDisabled: buttonIsDisabled,
        color: buttonColor,
        flat,
        block,
        label: placeholderOverride,
        ...restButtonProps
    } = buttonProps || {};
    const hasOptions = options?.length > 0;
    const placeholder = placeholderOverride ?? t('select.placeholder');
    const selectedLabels = (value ?? []).map((o) => getObjForValue(o)?.label).filter(Boolean);
    const dropdownLabel = !hasOptions
        ? t('select.empty')
        : selectedLabels.length === 0
          ? placeholder
          : selectedLabels.length === 1
            ? selectedLabels[0]
            : t('select.selectedCount', { count: selectedLabels.length });
    const dropdownDisabled = !hasOptions || buttonDisabled || buttonIsDisabled;

    const toggleValue = (key: Option['key']) => {
        const currentValues = value ?? [];
        const exists = currentValues.some((item) => String(item) === String(key));
        const newValue = exists
            ? currentValues.filter((item) => String(item) !== String(key))
            : [...currentValues, key];
        onChange(newValue);
    };

    return (
        <div className={cx('tw-my-4', className)}>
            {label && (
                <label className="tw-mn-2 tw-pr-2 tw-text-[14px]">
                    {label}
                    {required && ' *'}
                </label>
            )}
            <Dropdown
                {...restButtonProps}
                ref={dropdownRef}
                size="sm"
                label={dropdownLabel}
                block={block ?? true}
                flat={flat ?? true}
                color={errorMessage ? 'error' : (buttonColor ?? 'light')}
                disabled={dropdownDisabled}
                isDisabled={dropdownDisabled}
                className={cx('tw-flex tw-w-full !tw-justify-start tw-gap-2', dropdownButtonClassName)}
                placement="bottom"
                closeOnSelect={false}
                containerClassName={cx('tw-w-full', containerClassName)}
            >
                {options.map((item) => {
                    const isSelected = (value ?? []).some((selected) => String(selected) === String(item.key));

                    return (
                        <Dropdown.Item
                            key={item.key}
                            color={item.color}
                            className={cx(isSelected && 'tw-bg-accents-2')}
                            onClick={() => toggleValue(item.key)}
                        >
                            <FontAwesomeIcon icon={faCheck} className={cx(!isSelected && 'tw-invisible')} />
                            <span className="tw-flex tw-min-w-0 tw-items-center tw-gap-2">
                                {item.icon && <FontAwesomeIcon icon={item.icon} />}
                                <span className="tw-truncate">{item.label}</span>
                            </span>
                        </Dropdown.Item>
                    );
                })}
            </Dropdown>
            {errorMessage && <p className="tw-m-0 -tw-mt-1 tw-ml-3 tw-text-[10px] tw-text-error">{errorMessage}</p>}
            {description && <small className="tw-ml-4 tw-text-[9px] tw-text-accents-6">{description}</small>}
        </div>
    );
};

export default FormSelectMultiple;
